import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import TextField from '@atlaskit/textfield';
import messages from '../messages/confirmationForm';
import TallButton from '../../../../components/TallButton';
import ButtonContainer from '../../../../components/ButtonContainer';
import { ConsentCheckboxState } from '../../../../utilities/marketing-consent';
import FormError from '../../../../components/FormError';
import styled from '@emotion/styled';
import { gridSize } from '@atlaskit/theme';
import FieldGroup from '../../../../components//FieldGroup';
import { isAppleHiddenEmail } from '../../../../utilities/social-login';
import { colors } from '@atlaskit/theme';
import { ErrorMessage, Field, MessageWrapper } from '@atlaskit/form';
import { ChallengeResponse } from '../../../../contexts/recaptcha';
import { CreateAccountConfirmationMarketingConsent } from './CreateAccountConfirmationMarketingConsent';
import { useCreateAccountConfirmation } from '../hooks/useCreateAccountConfirmation';
import { CreateAccountConfirmationRecaptcha } from './Recapcha/Recaptcha';

export const PrivacyPolicyContainer = styled.div`
  margin-top: ${gridSize()}px;
  text-align: left;
`;

const ConsentAndLegalContainer = styled(FieldGroup)`
  margin-top: ${gridSize()}px;
`;

export const HiddenEmailWarning = styled.div`
  display: flex;
  margin-top: ${gridSize()}px;
`;

const StyledForm = styled.form`
  margin: 0;
`;

export const DisplayText = styled.div`
  font-weight: bold;
  color: ${colors.N800};
  word-wrap: break-word;
`;

const VerificationWrapper = styled.div`
  margin: ${gridSize() * 2}px 0 0;
`;

export interface ConfirmationFormData {
  displayName?: string;
  code?: string;
  marketingConsentState?: ConsentCheckboxState;
  challengeResponse?: ChallengeResponse | null;
}

export type ConfirmationFormProps = {
  privacyPolicy: React.ReactNode | undefined;
  isEmbedded?: boolean;
  isLoading?: boolean;
  title?: React.ReactNode;
  onSubmitClick: () => void;
};

export const ConfirmationForm = ({
  isLoading,
  onSubmitClick,
  privacyPolicy,
  title,
}: ConfirmationFormProps) => {
  const { formatMessage } = useIntl();

  const {
    onCreateAccountConfirmation,
    createAccountNeeded: createNeeded,
    verifyEmailNeeded: verifyNeeded,
    email,
    displayName: initialDisplayName,
    canEditDisplayName,
    formError,
    fieldErrors,
  } = useCreateAccountConfirmation({});

  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    if (initialDisplayName && displayName === '') {
      setDisplayName(initialDisplayName);
    }
  }, [initialDisplayName, displayName]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    await onCreateAccountConfirmation({ displayName, code });
  };

  const handleDisplayNameChange = (e: React.FormEvent<HTMLInputElement>) => {
    const displayName = e.currentTarget.value;
    setDisplayName(displayName);
  };

  const handleCodeChange = (e: React.FormEvent<HTMLInputElement>) => {
    const code = e.currentTarget.value;
    setCode(code);
  };

  const submitButtonText = () => {
    if (createNeeded) {
      return (
        <FormattedMessage
          id="confirmation.form.submit.create"
          defaultMessage="Create your account"
          description="Button to submit form and create account"
        />
      );
    } else {
      return (
        <FormattedMessage
          id="confirmation.form.submit.verify"
          defaultMessage="Verify your account"
          description="Button to submit form and verify account"
        />
      );
    }
  };

  const renderHiddenEmailWarning = () => {
    return (
      <HiddenEmailWarning>
        <FormError title={formatMessage(messages.hiddenEmailWarningTitle)}>
          <p data-testid="hidden-email-warning">
            <FormattedMessage
              id="confirmation.form.hiddenemailwarning.message"
              defaultMessage="Your team may not recognize you and you won't receive their updates. When you are ready to work with a team, just update your Apple ID settings to share your real email."
            />
          </p>
        </FormError>
      </HiddenEmailWarning>
    );
  };

  const renderCodeError = (error: string) => {
    switch (error) {
      case 'invalid_code': // server error
        return (
          <FormattedMessage
            id="confirmation.form.code.invalid.server"
            defaultMessage="Provided verification code is invalid"
          />
        );
      case 'invalid_length':
        return (
          <FormattedMessage
            id="confirmation.form.code.invalid.length"
            defaultMessage="Your verification code needs to be {length, plural, one {# digit} other {# digits}} or longer"
            values={{ length: 8 }}
          />
        );
      case 'empty_code':
        return (
          <FormattedMessage
            id="confirmation.form.code.empty"
            defaultMessage="Please enter a verification code"
          />
        );
    }
  };

  const displayNameError = fieldErrors && fieldErrors.displayName;
  const codeError = fieldErrors && fieldErrors.code;

  return (
    <div>
      {formError && <FormError>{formError?.message}</FormError>}
      {title && <div>{title}</div>}
      {email && isAppleHiddenEmail(email) && renderHiddenEmailWarning()}
      <StyledForm onSubmit={handleSubmit} id="confirmation-form">
        <Field name="email" label={formatMessage(messages.emailLabel)}>
          {() => <DisplayText>{email}</DisplayText>}
        </Field>

        {createNeeded && (
          <Field name="displayName" label={formatMessage(messages.displayNameLabel)}>
            {({ fieldProps }) =>
              !canEditDisplayName ? (
                <DisplayText role="caption">{initialDisplayName}</DisplayText>
              ) : (
                <>
                  <TextField
                    {...fieldProps}
                    placeholder={formatMessage(messages.displayNamePlaceholder)}
                    type="text"
                    defaultValue={initialDisplayName}
                    onChange={handleDisplayNameChange}
                    isInvalid={!!displayNameError}
                    autoFocus={!!email && !initialDisplayName}
                  />
                  {!!displayNameError && <ErrorMessage>{displayNameError}</ErrorMessage>}
                </>
              )
            }
          </Field>
        )}
        {verifyNeeded && (
          <VerificationWrapper>
            <Field name="code" label={formatMessage(messages.codeLabel)}>
              {({ fieldProps }) => (
                <>
                  <TextField
                    {...fieldProps}
                    id="code"
                    placeholder={formatMessage(messages.codePlaceHolder)}
                    type="text"
                    onChange={handleCodeChange}
                    isInvalid={!!codeError}
                    aria-describedby={codeError ? `${fieldProps.id}-error` : undefined}
                    spellCheck={false}
                    autoComplete="off"
                    maxLength={16}
                  />
                  <MessageWrapper>
                    {!!codeError && <ErrorMessage>{renderCodeError(codeError)}</ErrorMessage>}
                  </MessageWrapper>
                </>
              )}
            </Field>
          </VerificationWrapper>
        )}

        {createNeeded && (
          <ConsentAndLegalContainer>
            <CreateAccountConfirmationMarketingConsent />
            <PrivacyPolicyContainer>{privacyPolicy}</PrivacyPolicyContainer>
          </ConsentAndLegalContainer>
        )}

        <CreateAccountConfirmationRecaptcha />

        <ButtonContainer>
          <TallButton
            appearance="primary"
            id="login-submit"
            type="submit"
            isFullWidth
            isLoading={isLoading}
            onClick={onSubmitClick}>
            {submitButtonText()}
          </TallButton>
        </ButtonContainer>
      </StyledForm>
    </div>
  );
};

export default ConfirmationForm;
